/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { useReducer, useState, useContext, useEffect } from 'react';
import { observableValue, derived } from '@vscode/observables';
import { ManualChangesHandler } from './ManualChangesHandler.js';
import { unstable_batchedUpdates } from 'react-dom';

let renderingCount = 0;
const batchedUpdater = new ManualChangesHandler(potentialChanges => {
    for (const change of potentialChanges) {
        change.rendered = false;
    }
    if (renderingCount > 0) {
        const rendering = potentialChanges.find(c => c.isRendering);
        rendering.itemsToRender = potentialChanges;
    }
    else {
        unstable_batchedUpdates(() => {
            for (const change of potentialChanges) {
                if (!change.rendered) {
                    change.forceUpdate();
                }
            }
        });
    }
});
let viewIdx = 0;
class ViewImpl {
    constructor(debugName, renderFactory) {
        this.debugName = debugName;
        this.renderFactory = renderFactory;
        this._obsProps = undefined;
        this.forceUpdate = undefined;
        this._render = undefined;
        this._disposable = undefined;
        this.contextValues = new Map();
        this.cleanupEffect = () => {
            return () => {
                this._disposable?.dispose();
            };
        };
        this.itemsToRender = [];
        this.handleAfterRender = () => {
            unstable_batchedUpdates(() => {
                for (const change of this.itemsToRender) {
                    if (!change.rendered) {
                        change.forceUpdate();
                    }
                }
            });
            this.itemsToRender = [];
        };
        this.rendering = undefined;
        this.isRendering = false;
        this.rendered = false;
    }
    toString() {
        return this.debugName;
    }
    updateProps(props) {
        if (!this._obsProps) {
            this._obsProps = observableValue(this, props);
            this._render = this.renderFactory(this._obsProps, () => this.contextValues);
            this.rendering = derived(this, this._render);
            this._disposable = batchedUpdater.addDependency(this.rendering, this);
        }
        else {
            this._obsProps.set(props, undefined);
        }
    }
}
function obsView(componentName, render, contexts) {
    const plusOne = (x) => x + 1;
    const createVM = () => new ViewImpl(componentName + (++viewIdx), render);
    const fn = function (props) {
        const forceUpdate = useReducer(plusOne, 0)[1];
        const state = useState(createVM)[0];
        // Read and update context values each render
        for (const ctx of contexts ?? []) {
            state.contextValues.set(ctx, useContext(ctx)); // eslint-disable-line react-hooks/rules-of-hooks
        }
        useEffect(state.cleanupEffect, []);
        useEffect(state.handleAfterRender);
        if (state.isRendering) {
            throw new Error("Component is already rendering");
        }
        state.isRendering = true;
        renderingCount++;
        try {
            state.forceUpdate = forceUpdate;
            state.updateProps(props);
            const result = state.rendering.get();
            state.rendered = true;
            return result;
        }
        finally {
            state.isRendering = false;
            renderingCount--;
        }
    };
    fn.displayName = componentName;
    return fn;
}
const ObsView = obsView('ObsView', (props) => reader => props.read(reader).children(reader));

export { ObsView, obsView };
//# sourceMappingURL=obsView.js.map
